/**
 * BYO-key credential store. greenbook ships with NO key: each user connects their own
 * OpenMarkets API key. Since greenbook is pure-static (no server), the browser talks
 * straight to the OpenMarkets API — the key is held in localStorage and sent as `X-API-Key`
 * by the client (see client.ts) on REST calls and in the `?api_key=` query on the WS stream.
 *
 * SECURITY: a raw API key in localStorage is readable by any script on this origin — the
 * least-secure option (documented in FRICTION.md). A dashboard-minted, short-lived, revocable
 * token handed off from app.openmarkets.ai is the intended replacement; this store is the seam
 * for it (swap what `setToken` receives; nothing downstream changes).
 */
import { useSyncExternalStore } from "react";

const KEY = "gb.token";

function read(): string | null {
  try {
    return localStorage.getItem(KEY);
  } catch {
    return null;
  }
}

let token: string | null = read();
const listeners = new Set<() => void>();
function emit(): void {
  for (const l of listeners) l();
}

export function getToken(): string | null {
  return token;
}

export function setToken(next: string | null): void {
  token = next && next.trim() ? next.trim() : null;
  try {
    if (token) localStorage.setItem(KEY, token);
    else localStorage.removeItem(KEY);
  } catch {
    /* ignore storage failures — token still lives in memory for this session */
  }
  emit();
}

export function clearToken(): void {
  setToken(null);
}

export const tokenStore = {
  subscribe(fn: () => void): () => void {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },
  getSnapshot(): string | null {
    return token;
  },
};

/** Reactively read the current token (null when disconnected → app shows the connect gate). */
export function useToken(): string | null {
  return useSyncExternalStore(tokenStore.subscribe, tokenStore.getSnapshot);
}

export function useHasToken(): boolean {
  return useToken() !== null;
}
